import { html, type PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'

import styles from './overlay.scss?inline'

/**
 * `dap-ds-overlay`
 * @summary An overlay is a container for overlay content.
 * @element dap-ds-overlay
 * @title - Overlay
 *
 * @property {boolean} open - The open state of the overlay.
 *
 * @event {CustomEvent} dds-before-open - Fires before the overlay opens.
 * @event {CustomEvent} dds-opened - Fires after the overlay opens.
 * @event {CustomEvent} dds-before-close - Fires before the overlay closes.
 * @event {CustomEvent} dds-closed - Fires after the overlay closes.
 *
 * @slot - The content of the overlay.
 *
 * @csspart base - The overlay element
 *
 * @cssproperty --dds-overlay-z-index - Controls the z-index of the overlay (default: 1)
 * @cssproperty --dds-overlay-background - Controls the background of the overlay (default: var(--dds-black-10))
 * @cssproperty --dds-overlay-opacity-closed - Controls the opacity when overlay is closed (default: 0)
 * @cssproperty --dds-overlay-opacity-open - Controls the opacity when overlay is open (default: 1)
 * @cssproperty --dds-overlay-transition-duration - Controls the transition duration (default: var(--dds-transition-fast))
 * @cssproperty --dds-overlay-transition-timing - Controls the transition timing function (default: var(--dds-easing-ease-in-out))
 */
@customElement('dap-ds-overlay')
export default class DapDSOverlay extends DdsElement {
  /** The open state of the overlay. */
  @property({ type: Boolean, reflect: true }) open = false

  static readonly styles = unsafeCSS(styles)

  connectedCallback(): void {
    super.connectedCallback()
    // Set aria-hidden to true by default
    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true')
    }
    // Add closed class initially
    if (!this.open) {
      this.classList.add('closed')
    }
  }

  protected updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(changedProperties)

    if (changedProperties.has('open')) {
      const wasOpen = changedProperties.get('open') as boolean | undefined
      const isOpen = this.open

      if (isOpen && !wasOpen) {
        this.handleOpen()
      } else if (!isOpen && wasOpen) {
        this.handleClose()
      }
    }
  }

  private handleOpen(): void {
    const beforeOpenEvent = this.emit('dds-before-open', undefined, {
      cancelable: true,
    })

    if (beforeOpenEvent.defaultPrevented) {
      this.open = false
      return
    }

    this.classList.remove('closed', 'transitioning')
    this.setAttribute('aria-hidden', 'false')

    this.addEventListener(
      'transitionend',
      () => {
        this.emit('dds-opened', undefined, {
          cancelable: false,
        })
      },
      { once: true },
    )
  }

  private handleClose(): void {
    const beforeCloseEvent = this.emit('dds-before-close', undefined, {
      cancelable: true,
    })

    if (beforeCloseEvent.defaultPrevented) {
      this.open = true
      return
    }

    this.classList.add('transitioning')
    this.classList.remove('closed')
    this.setAttribute('aria-hidden', 'true')

    this.addEventListener(
      'transitionend',
      () => {
        this.classList.remove('transitioning')
        this.classList.add('closed')
        this.emit('dds-closed', undefined, {
          cancelable: false,
        })
      },
      { once: true },
    )
  }

  render() {
    return html`<div part="base"><slot></slot></div>`
  }
}
